import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  $isNodeSelection,
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  $createParagraphNode,
} from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { mergeRegister, $getNearestNodeOfType } from '@lexical/utils';
import { createPortal } from 'react-dom';
import { $isImageNode } from '../../LexicalEditor/nodes/ImageNode';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode,
} from '@lexical/list';
import { $createHeadingNode, $isHeadingNode } from '@lexical/rich-text';
import { $createQuoteNode } from '@lexical/rich-text';
import { $createCodeNode } from '@lexical/code';
import { $wrapNodes, $isAtNodeEnd, $patchStyleText } from '@lexical/selection';
import useHandlePopoverClick from '../../../hooks/useHandlePopoverClick';
import {
  FormatBold,
  FormatItalic,
  Link,
  MoreHoriz,
  MatchCase,
  KeyboardArrowDown,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Add,
  FormatH1,
  FormatH2,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Code,
  ClearAll,
} from '../../icons';
import { TEXT_COLORS, BACKGROUND_COLORS } from '../utils/colorConstants';
import { INSERT_COMMENT_COMMAND } from './CommentsPlugin';
import { MessageSquare } from 'lucide-react';
import './scss/PageFloatingToolbar.scss';

const LowPriority = 1;

function getSelectedNode(selection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}

function positionToolbar(toolbar, rect) {
  if (rect === null) {
    toolbar.style.opacity = '0';
    toolbar.style.top = '-1000px';
    toolbar.style.left = '-1000px';
    toolbar.style.pointerEvents = 'none';
  } else {
    toolbar.style.opacity = '1';
    toolbar.style.pointerEvents = 'auto';
    
    // Position above the selection
    const top = rect.top + window.pageYOffset - toolbar.offsetHeight - 10;
    let left = rect.left + window.pageXOffset - toolbar.offsetWidth / 2 + rect.width / 2;
    
    // Keep toolbar within viewport
    const viewportWidth = window.innerWidth;
    const toolbarWidth = toolbar.offsetWidth;
    
    if (left < 10) {
      left = 10;
    } else if (left + toolbarWidth > viewportWidth - 10) {
      left = viewportWidth - toolbarWidth - 10;
    }
    
    toolbar.style.top = `${top}px`;
    toolbar.style.left = `${left}px`;
  }
}

function Divider() {
  return <div className="page-toolbar-divider" />;
}

function PageFloatingToolbar({ editor }) {
  const toolbarRef = useRef(null);
  const linkInputRef = useRef(null);
  const blockMenuRef = useRef(null);
  const alignMenuRef = useRef(null);
  
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [blockType, setBlockType] = useState('paragraph');
  
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [lastSelection, setLastSelection] = useState(null);
  const [showBlockMenu, setShowBlockMenu] = useState(false);
  const [showAlignMenu, setShowAlignMenu] = useState(false);
  const [showColorMenu, setShowColorMenu] = useState(false);
  const [textColor, setTextColor] = useState('');
  const [bgColor, setBgColor] = useState('');

  const colorMenuRef = useRef(null);

  const { handlePopoverClick } = useHandlePopoverClick();

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    const toolbarElem = toolbarRef.current;
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (toolbarElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    if (
      selection !== null &&
      $isRangeSelection(selection) &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const domRange = nativeSelection.getRangeAt(0);
      let rect;
      if (nativeSelection.anchorNode === rootElement) {
        let inner = rootElement;
        while (inner.firstElementChild !== null) {
          inner = inner.firstElementChild;
        }
        rect = inner.getBoundingClientRect();
      } else {
        rect = domRange.getBoundingClientRect();
      }

      positionToolbar(toolbarElem, rect);

      // Update format states
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsCode(selection.hasFormat('code'));

      // Update block type
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();

      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType(anchorNode, ListNode);
        const type = parentList ? parentList.getTag() : element.getTag();
        setBlockType(type);
      } else {
        const type = $isHeadingNode(element)
          ? element.getTag()
          : element.getType();
        setBlockType(type);
      }

      // Update link state
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
        const linkNode = $isLinkNode(parent) ? parent : node;
        setLinkUrl(linkNode.getURL());
      } else {
        setIsLink(false);
        setLinkUrl('');
      }

      setLastSelection(selection);
    } else if (!activeElement || !activeElement.closest('.page-floating-toolbar')) {
      positionToolbar(toolbarElem, null);
      setShowLinkInput(false);
      setShowBlockMenu(false);
      setShowAlignMenu(false);
      setLastSelection(null);
    }

    return true;
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  // Format handlers
  const formatText = (format) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const applyTextColor = (color) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (color === '') {
          // Remove color style
          const nodes = selection.getNodes();
          nodes.forEach((node) => {
            if (node.getType() === 'text') {
              const currentStyle = node.getStyle();
              const newStyle = currentStyle.replace(/color:\s*[^;]+;?/g, '').trim();
              node.setStyle(newStyle);
            }
          });
        } else {
          $patchStyleText(selection, { color });
        }
      }
    });
    setTextColor(color);
    setShowColorMenu(false);
  };

  const applyHighlight = (color) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (color === '') {
          // Remove background-color style
          const nodes = selection.getNodes();
          nodes.forEach((node) => {
            if (node.getType() === 'text') {
              const currentStyle = node.getStyle();
              const newStyle = currentStyle.replace(/background-color:\s*[^;]+;?/g, '').trim();
              node.setStyle(newStyle);
            }
          });
        } else {
          $patchStyleText(selection, { 'background-color': color });
        }
      }
    });
    setBgColor(color);
    setShowColorMenu(false);
  };

  const clearFormatting = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes();

        nodes.forEach((node) => {
          if (node.getType() === 'text') {
            // Clear all text formatting
            node.setFormat(0);
            // Clear inline styles (color, background-color, etc.)
            node.setStyle('');
          }
        });
      }
    });
    setTextColor('');
    setBgColor('');
  };

  const insertLink = () => {
    if (!isLink) {
      setShowLinkInput(true);
      setLinkUrl('');
      setTimeout(() => {
        if (linkInputRef.current) {
          linkInputRef.current.focus();
        }
      }, 0);
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      setShowLinkInput(false);
    }
  };

  const handleLinkSubmit = () => {
    if (linkUrl.trim() && lastSelection) {
      let normalizedUrl = linkUrl.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = `https://${normalizedUrl}`;
      }

      editor.update(() => {
        if (lastSelection) {
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, normalizedUrl);
        }
      });
    }

    setShowLinkInput(false);
    setLinkUrl('');
  };

  const handleLinkKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleLinkSubmit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setShowLinkInput(false);
      setLinkUrl('');
    }
  };

  // Block format handlers
  const formatParagraph = () => {
    if (blockType !== 'paragraph') {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createParagraphNode());
        }
      });
    }
    setShowBlockMenu(false);
  };

  const formatHeading = (tag) => {
    if (blockType !== tag) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createHeadingNode(tag));
        }
      });
    }
    setShowBlockMenu(false);
  };

  const formatBulletList = () => {
    if (blockType !== 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND);
    }
    setShowBlockMenu(false);
  };

  const formatNumberedList = () => {
    if (blockType !== 'ol') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND);
    }
    setShowBlockMenu(false);
  };

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createQuoteNode());
        }
      });
    }
    setShowBlockMenu(false);
  };

  const formatCode = () => {
    if (blockType !== 'code') {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createCodeNode());
        }
      });
    }
    setShowBlockMenu(false);
  };

  const formatAlign = (alignment) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isNodeSelection(selection)) {
        const nodes = selection.getNodes();
        nodes.forEach((node) => {
          if ($isImageNode(node)) {
            node.setAlignment(alignment);
          }
        });
      } else {
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignment);
      }
    });
    setShowAlignMenu(false);
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (blockMenuRef.current && !blockMenuRef.current.contains(event.target)) {
        setShowBlockMenu(false);
      }
      if (alignMenuRef.current && !alignMenuRef.current.contains(event.target)) {
        setShowAlignMenu(false);
      }
      if (colorMenuRef.current && !colorMenuRef.current.contains(event.target)) {
        setShowColorMenu(false);
      }
    };

    if (showBlockMenu || showAlignMenu || showColorMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showBlockMenu, showAlignMenu, showColorMenu]);

  return (
    <div ref={toolbarRef} className="page-floating-toolbar">
      <div className="page-toolbar-content">
        {/* Block Type */}
        <div className="page-toolbar-dropdown-wrapper">
          <button
            type="button"
            onClick={() => setShowBlockMenu(!showBlockMenu)}
            className="page-toolbar-button page-toolbar-dropdown-trigger"
            aria-label="Block Type"
            title="Block Type"
          >
            <MatchCase size={18} color="var(--editor-toolbar-icon-color)" />
            <KeyboardArrowDown size={14} color="var(--editor-toolbar-icon-color)" />
          </button>
          {showBlockMenu && (
            <div ref={blockMenuRef} className="page-toolbar-dropdown">
              <button type="button" className="page-toolbar-dropdown-item" onClick={formatParagraph}>
                <FormatAlignLeft size={18} color="var(--editor-toolbar-icon-color)" />
                <span>Normal</span>
              </button>
              <button type="button" className="page-toolbar-dropdown-item" onClick={() => formatHeading('h1')}>
                <FormatH1 size={18} color="var(--editor-toolbar-icon-color)" />
                <span>Large Heading</span>
              </button>
              <button type="button" className="page-toolbar-dropdown-item" onClick={() => formatHeading('h2')}>
                <FormatH2 size={18} color="var(--editor-toolbar-icon-color)" />
                <span>Small Heading</span>
              </button>
              <button type="button" className="page-toolbar-dropdown-item" onClick={formatBulletList}>
                <FormatListBulleted size={18} color="var(--editor-toolbar-icon-color)" />
                <span>Bullet List</span>
              </button>
              <button type="button" className="page-toolbar-dropdown-item" onClick={formatNumberedList}>
                <FormatListNumbered size={18} color="var(--editor-toolbar-icon-color)" />
                <span>Numbered List</span>
              </button>
              <button type="button" className="page-toolbar-dropdown-item" onClick={formatQuote}>
                <FormatQuote size={18} color="var(--editor-toolbar-icon-color)" />
                <span>Quote</span>
              </button>
              <button type="button" className="page-toolbar-dropdown-item" onClick={formatCode}>
                <Code size={18} color="var(--editor-toolbar-icon-color)" />
                <span>Code Block</span>
              </button>
            </div>
          )}
        </div>

        <Divider />

        {/* Text Formatting */}
        <button
          type="button"
          onClick={() => formatText('bold')}
          className={`page-toolbar-button ${isBold ? 'active' : ''}`}
          aria-label="Bold"
          title="Bold"
        >
          <FormatBold size={18} color="var(--editor-toolbar-icon-color)" />
        </button>
        <button
          type="button"
          onClick={() => formatText('italic')}
          className={`page-toolbar-button ${isItalic ? 'active' : ''}`}
          aria-label="Italic"
          title="Italic"
        >
          <FormatItalic size={18} color="var(--editor-toolbar-icon-color)" />
        </button>
        <button
          type="button"
          onClick={(e) => handlePopoverClick(e, 'editorTransform', {
            editor,
            isStrikethrough,
            isUnderline,
            isCode,
            isClosed: false,
          })}
          id="page-editor-transform-button"
          className="page-toolbar-button"
          aria-label="More formatting"
          title="More formatting"
          data-popover-trigger
        >
          <MoreHoriz size={18} color="var(--editor-toolbar-icon-color)" />
        </button>

        <Divider />

        {/* Text Color & Highlight */}
        <div className="page-toolbar-dropdown-wrapper">
          <button
            type="button"
            onClick={() => setShowColorMenu(!showColorMenu)}
            className="page-toolbar-button page-toolbar-dropdown-trigger"
            aria-label="Text Color"
            title="Text Color & Highlight"
          >
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>A</span>
            <KeyboardArrowDown size={14} color="var(--editor-toolbar-icon-color)" />
          </button>
          {showColorMenu && (
            <div ref={colorMenuRef} className="page-toolbar-dropdown page-toolbar-color-picker">
              <div className="page-toolbar-color-section">
                <div className="page-toolbar-color-label">Text Color</div>
                <div className="page-toolbar-color-grid">
                  {TEXT_COLORS.map((color) => (
                    <button
                      key={color.key}
                      type="button"
                      className={`page-toolbar-color-swatch ${color.key === 'default' ? 'page-toolbar-color-default' : ''}`}
                      style={color.value ? { color: color.value } : {}}
                      onClick={() => applyTextColor(color.value)}
                      title={color.label}
                    >
                      A
                    </button>
                  ))}
                </div>
              </div>
              <div className="page-toolbar-color-section">
                <div className="page-toolbar-color-label">Highlight</div>
                <div className="page-toolbar-color-grid">
                  {BACKGROUND_COLORS.map((color) => (
                    <button
                      key={color.key}
                      type="button"
                      className={`page-toolbar-color-swatch ${color.key === 'default' ? 'page-toolbar-color-clear' : ''}`}
                      style={color.value ? { backgroundColor: color.value } : {}}
                      onClick={() => applyHighlight(color.value)}
                      title={color.label}
                    >
                      {color.key === 'default' ? '✕' : ''}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <Divider />

        {/* Clear Formatting */}
        <button
          type="button"
          onClick={clearFormatting}
          className="page-toolbar-button"
          aria-label="Clear Formatting"
          title="Clear Formatting"
        >
          <ClearAll size={18} color="var(--editor-toolbar-icon-color)" />
        </button>

        <Divider />

        {/* Link */}
        <button
          type="button"
          onClick={insertLink}
          className={`page-toolbar-button ${isLink ? 'active' : ''}`}
          aria-label="Link"
          title="Link"
        >
          <Link size={18} color="var(--editor-toolbar-icon-color)" />
        </button>

        {/* Comment */}
        <button
          type="button"
          onClick={() => editor.dispatchCommand(INSERT_COMMENT_COMMAND, undefined)}
          className="page-toolbar-button"
          aria-label="Add comment"
          title="Add comment (Ctrl+Alt+M)"
        >
          <MessageSquare size={16} color="var(--editor-toolbar-icon-color)" />
        </button>

        {/* Alignment */}
        <div className="page-toolbar-dropdown-wrapper">
          <button
            type="button"
            onClick={() => setShowAlignMenu(!showAlignMenu)}
            className="page-toolbar-button page-toolbar-dropdown-trigger"
            aria-label="Alignment"
            title="Alignment"
          >
            <Add size={18} color="var(--editor-toolbar-icon-color)" />
            <KeyboardArrowDown size={14} color="var(--editor-toolbar-icon-color)" />
          </button>
          {showAlignMenu && (
            <div ref={alignMenuRef} className="page-toolbar-dropdown">
              <button type="button" className="page-toolbar-dropdown-item" onClick={() => formatAlign('left')}>
                <FormatAlignLeft size={18} color="var(--editor-toolbar-icon-color)" />
                <span>Left</span>
              </button>
              <button type="button" className="page-toolbar-dropdown-item" onClick={() => formatAlign('center')}>
                <FormatAlignCenter size={18} color="var(--editor-toolbar-icon-color)" />
                <span>Center</span>
              </button>
              <button type="button" className="page-toolbar-dropdown-item" onClick={() => formatAlign('right')}>
                <FormatAlignRight size={18} color="var(--editor-toolbar-icon-color)" />
                <span>Right</span>
              </button>
              <button type="button" className="page-toolbar-dropdown-item" onClick={() => formatAlign('justify')}>
                <FormatAlignJustify size={18} color="var(--editor-toolbar-icon-color)" />
                <span>Justify</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Link Input */}
      {showLinkInput && (
        <div className="page-toolbar-link-input">
          <input
            ref={linkInputRef}
            type="text"
            className="page-toolbar-link-input-field"
            placeholder="Paste or type URL..."
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={handleLinkKeyDown}
            onBlur={() => {
              setTimeout(() => setShowLinkInput(false), 150);
            }}
          />
          <button
            className="page-toolbar-link-submit"
            onClick={handleLinkSubmit}
            title="Apply Link"
          >
            ✓
          </button>
        </div>
      )}
    </div>
  );
}

export default function PageFloatingToolbarPlugin() {
  const [editor] = useLexicalComposerContext();

  return createPortal(<PageFloatingToolbar editor={editor} />, document.body);
}

